import { useEffect } from 'react'
import type { Driver } from 'driver.js'
import { getDriver } from '@/utils/driver/driverSingleton'
import { renderPopoverFooter } from '@/utils/driver/renderPopoverFooter'

let walkthroughStartStep = 1
let walkthroughTotalSteps = 9

let appWalkthroughConfigCache: ReturnType<Driver['getConfig']> | null = null
let onExplicitDismissCallback: (() => void) | null = null

export const setWalkthroughStepInfo = (
    startStep: number,
    totalSteps: number
) => {
    walkthroughStartStep = startStep
    walkthroughTotalSteps = totalSteps
}

export const getWalkthroughStartStep = () => {
    return walkthroughStartStep
}

export const setOnExplicitDismissCallback = (callback: (() => void) | null) => {
    onExplicitDismissCallback = callback
}

export const getOnExplicitDismissCallback = (): (() => void) | null => {
    return onExplicitDismissCallback
}

export const cacheAppWalkthroughConfig = (driver: Driver) => {
    appWalkthroughConfigCache = driver.getConfig()
}

const addHighlightEffect = (element: HTMLElement) => {
    element.classList.add('is-highlighted')
}

const removeHighlightEffect = (element: HTMLElement) => {
    element.classList.remove('is-highlighted')
}

const stepMultiHighlightMap = new Map<number, string[]>()
const stepInteractionModeMap = new Map<number, boolean>()

export const registerStepMultiHighlight = (
    stepIndex: number,
    selectors: string[]
) => {
    stepMultiHighlightMap.set(stepIndex, selectors)
}

export const registerStepInteractionMode = (
    stepIndex: number,
    allowInteraction: boolean
) => {
    stepInteractionModeMap.set(stepIndex, allowInteraction)
}

export const clearStepMultiHighlights = () => {
    stepMultiHighlightMap.clear()
    stepInteractionModeMap.clear()
}

export const ensureAppWalkthroughConfig = (driver: Driver) => {
    if (appWalkthroughConfigCache) {
        driver.setConfig(appWalkthroughConfigCache)
    }
}

export const useAppWalkthroughDriverConfig = (): Driver => {
    const driver = getDriver()

    useEffect(() => {
        driver.setConfig({
            stagePadding: 0,
            stageRadius: 8,
            popoverOffset: 4,
            popoverClass: 'app-walkthrough-popover',
            overlayOpacity: 0.4,
            allowClose: false,
            showButtons: ['next', 'close'],
            showProgress: true,
            progressText: '{{current}} of {{total}}',
            onPopoverRender: (popover, opts) => {
                popover.closeButton.style.display = 'none'

                const driverIndex = opts.driver.getActiveIndex() ?? 0
                const currentGlobalStep = walkthroughStartStep + driverIndex
                const progressElement = popover.progress
                progressElement.innerHTML = `<strong>${String(currentGlobalStep)}</strong> of ${String(walkthroughTotalSteps)}`

                const config = opts.driver.getConfig()

                renderPopoverFooter({ popover, opts, config })
            },
            onHighlighted: (element, _step, opts) => {
                if (!element || !document.body.contains(element)) {
                    opts.driver.destroy()
                    return
                }

                const currentStepIndex = driver.getActiveIndex() ?? 0

                const allowInteraction =
                    stepInteractionModeMap.get(currentStepIndex) ?? false
                if (!allowInteraction) {
                    element.classList.add('driver-no-interaction')
                }

                const additionalSelectors =
                    stepMultiHighlightMap.get(currentStepIndex)
                if (additionalSelectors) {
                    additionalSelectors.forEach((selector: string) => {
                        const el = document.querySelector(selector)
                        if (el && el !== element) {
                            addHighlightEffect(el as HTMLElement)
                            if (!allowInteraction) {
                                el.classList.add('driver-no-interaction')
                            }
                        }
                    })
                }
            },
            onDeselected: (element, _step, _opts) => {
                if (!element) {
                    return
                }
                removeHighlightEffect(element as HTMLElement)
                element.classList.remove('driver-no-interaction')

                const currentStepIndex = driver.getActiveIndex() ?? 0

                const additionalSelectors =
                    stepMultiHighlightMap.get(currentStepIndex)
                if (additionalSelectors) {
                    additionalSelectors.forEach((selector: string) => {
                        const el = document.querySelector(selector)
                        if (el && el !== element) {
                            removeHighlightEffect(el as HTMLElement)
                            el.classList.remove('driver-no-interaction')
                        }
                    })
                }
            },
        })

        cacheAppWalkthroughConfig(driver)
    }, [driver])

    return driver
}
